import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  getLiveHealthOverview,
  type LiveCheck,
  type LiveCheckStatus,
} from "@/lib/live-health.functions";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  MinusCircle,
  RefreshCw,
  Activity,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/live-health")({
  component: LiveHealthPage,
  head: () => ({ meta: [{ title: "Live-Health — InboxPulse" }] }),
});

function LiveHealthPage() {
  const fn = useServerFn(getLiveHealthOverview);
  const { data, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["live-health"],
    queryFn: () => fn(),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const okCount = data?.checks.filter((c) => c.status === "ok").length ?? 0;
  const warnCount = data?.checks.filter((c) => c.status === "warn").length ?? 0;
  const failCount = data?.checks.filter((c) => c.status === "fail").length ?? 0;

  return (
    <div className="p-8">
      <PageHeader
        title="Live-Health"
        description="Selbst-Tests für Tracking-Pixel, Event-Ingestion und Queue-Status. Aktualisiert sich alle 15 Sekunden."
      />

      <HelpPanel
        title="Hilfe: Läuft alles?"
        what={
          <>
            Eine <b>Ampel-Übersicht</b>: alle wichtigen Teile des Systems werden automatisch
            getestet. Grün = ok, gelb = langsam, rot = kaputt.
          </>
        }
        why={
          <>
            Bevor du Fehler suchst, hier kurz reinschauen — dann weißt du sofort, ob das Problem bei
            dir oder im System liegt.
          </>
        }
        how={[
          <>Seite einfach öffnen — Tests laufen automatisch alle 15 Sekunden.</>,
          <>Roter Eintrag = klicken für Details und Fehlermeldung.</>,
        ]}
        tips={[
          <>
            Alles grün und trotzdem Probleme? Dann liegt es am eigenen Setup (z.B. Pixel nicht
            eingebaut).
          </>,
        ]}
        glossary={[
          {
            term: "Ingestion",
            def: "Aufnahme: das System nimmt eingehende Daten an und legt sie in die Warteschlange.",
          },
          { term: "Selbst-Test", def: "Das System ruft sich selbst auf und prüft die Antwort." },
        ]}
      />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Badge
          variant="outline"
          className={
            data?.ok
              ? "border-success/40 text-success"
              : failCount > 0
                ? "border-destructive/40 text-destructive"
                : "border-warning/40 text-warning"
          }
        >
          <Activity className="size-3 mr-1" />
          {data?.ok ? "Alle Systeme grün" : failCount > 0 ? "Fehler aktiv" : "Warnungen aktiv"}
        </Badge>
        {data && (
          <span className="text-xs text-muted-foreground">
            {okCount} ok · {warnCount} warn · {failCount} fail · in {data.totalMs}ms
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="ml-auto"
        >
          <RefreshCw className={`size-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`} />
          Jetzt neu prüfen
        </Button>
      </div>

      {error && (
        <Card className="p-4 mb-4 border-destructive/40 bg-destructive/5 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {(data?.checks ?? []).map((c) => (
          <CheckCard key={c.key} check={c} />
        ))}
        {!data && !error && (
          <Card className="p-8 text-center text-sm text-muted-foreground bg-card/40 col-span-full">
            Prüfungen werden ausgeführt…
          </Card>
        )}
      </div>

      {dataUpdatedAt > 0 && (
        <p className="mt-6 text-[11px] text-muted-foreground text-right">
          Zuletzt aktualisiert: {new Date(dataUpdatedAt).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}

function statusVisual(status: LiveCheckStatus) {
  switch (status) {
    case "ok":
      return { Icon: CheckCircle2, color: "text-success", border: "border-success/30" };
    case "warn":
      return { Icon: AlertTriangle, color: "text-warning", border: "border-warning/30" };
    case "fail":
      return { Icon: XCircle, color: "text-destructive", border: "border-destructive/40" };
    default:
      return { Icon: MinusCircle, color: "text-muted-foreground", border: "border-border" };
  }
}

function CheckCard({ check }: { check: LiveCheck }) {
  const v = statusVisual(check.status);
  return (
    <Card className={`p-4 bg-card/60 ${v.border}`}>
      <div className="flex items-start gap-3">
        <v.Icon className={`size-5 mt-0.5 shrink-0 ${v.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{check.label}</span>
            <Badge variant="outline" className={`text-[10px] ${v.color} ${v.border}`}>
              {check.status}
            </Badge>
            {typeof check.ms === "number" && (
              <span className="text-[11px] text-muted-foreground">{check.ms}ms</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1 break-words">{check.detail}</p>
          {check.meta && Object.keys(check.meta).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(check.meta).map(([k, val]) => (
                <span
                  key={k}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground font-mono"
                >
                  {k}={String(val)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
